import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  GraduationCap,
  ListChecks,
  KeyRound,
  LogOut,
  Menu,
  X,
  Users,
  UserSquare,
  Landmark,
  FileText,
  MapPin,
  BookOpen,
  Images,
  IdCard,
  ShieldCheck,
  BarChart3,
  CalendarRange,
  Calendar,
  Ticket,
  Award,
  Newspaper,
  Inbox,
  Briefcase,
  Wallet,
  Camera,
  Car,
  UserCog,
  Sparkles,
  ChevronRight,
  Globe,
  Wrench,
  Timer,
  LayoutTemplate,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  editor: "Editor",
  viewer: "Viewer",
  module_manager: "Module Manager",
  infrastructure: "Infrastructure",
  vehicle_admin: "Vehicle Maintenance Admin",
};

// Each item declares the module(s) needed to view it. `module: null` = always visible to authenticated.
// Super-admin navigation tree.
// Dashboard is pinned on top; every other item (groups + standalone links) is
// sorted alphabetically. Sub-links inside each group are sorted alphabetically
// too. This is only used when the signed-in user is `super_admin` — all other
// roles keep their existing flat sidebars powered by `navItems`.
const SUPER_ADMIN_NAV = [
  { kind: "link", to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { kind: "link", to: "/admin/change-password", label: "Change Password", icon: KeyRound, testid: "nav-change-password" },
  {
    kind: "group", label: "Infrastructure", icon: Wrench, testid: "nav-group-infrastructure",
    children: [
      { to: "/admin/amenities", label: "Amenities", icon: Sparkles, testid: "nav-amenities" },
      { to: "/admin/amenity-bookings", label: "Amenity Bookings", icon: Calendar, testid: "nav-amenity-bookings" },
      { to: "/admin/payments", label: "Payments", icon: Wallet, testid: "nav-payments" },
      { to: "/admin/tenders", label: "Tender Management", icon: FileText, testid: "nav-tenders" },
      { to: "/admin/vendors", label: "Vendor Management", icon: Building2, testid: "nav-vendors" },
      { to: "/admin/amenity-vouchers", label: "Waiver Vouchers", icon: Ticket, testid: "nav-amenity-vouchers" },
    ],
  },
  {
    kind: "group", label: "Marathon", icon: Timer, testid: "nav-group-marathon",
    children: [
      { to: "/admin/marathon-landing", label: "Landing Page", icon: LayoutTemplate, testid: "nav-marathon-landing" },
      { to: "/admin/marathon-registrations", label: "Registrations", icon: Users, testid: "nav-marathon-registrations" },
      { to: "/admin/marathon-runs", label: "Run Types & Fees", icon: Timer, testid: "nav-marathon-runs" },
    ],
  },
  { kind: "link", to: "/admin/masters", label: "Masters", icon: ListChecks, testid: "nav-masters" },
  { kind: "link", to: "/admin/analytics", label: "Site Analytics", icon: BarChart3, testid: "nav-analytics" },
  {
    kind: "group", label: "Trust Data", icon: Landmark, testid: "nav-group-trust-data",
    children: [
      { to: "/admin/academic", label: "Academic Reports", icon: BarChart3, testid: "nav-academic" },
      { to: "/admin/banks", label: "Bank Accounts", icon: Landmark, testid: "nav-banks" },
      { to: "/admin/meetings", label: "Board Meetings", icon: CalendarRange, testid: "nav-meetings" },
      { to: "/admin/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
      { to: "/admin/executive-body", label: "Executive Body", icon: UserSquare, testid: "nav-executive" },
      { to: "/admin/financials", label: "Financials", icon: BookOpen, testid: "nav-financials" },
      { to: "/admin/general-body", label: "General Body", icon: Users, testid: "nav-general" },
      { to: "/admin/banks/institutes", label: "Institute Bank", icon: Landmark, testid: "nav-banks-institutes" },
      { to: "/admin/institutes", label: "Institutes", icon: GraduationCap, testid: "nav-institutes" },
      { to: "/admin/properties", label: "Properties", icon: MapPin, testid: "nav-properties" },
      { to: "/admin/trust", label: "Trust Details", icon: Building2, testid: "nav-trust" },
    ],
  },
  { kind: "link", to: "/admin/users", label: "Users and Roles", icon: ShieldCheck, testid: "nav-users" },
  {
    kind: "group", label: "Vehicle Maintenance", icon: Car, testid: "nav-group-vehicle-maintenance",
    children: [
      { to: "/admin/drivers", label: "Drivers", icon: UserCog, testid: "nav-drivers" },
      { to: "/admin/vehicles", label: "Vehicle Master", icon: Car, testid: "nav-vehicles" },
    ],
  },
  {
    kind: "group", label: "Website Update", icon: Globe, testid: "nav-group-website-update",
    children: [
      { to: "/admin/admission-inquiries", label: "Admission Inquiries", icon: Inbox, testid: "nav-admission-inquiries" },
      { to: "/admin/employees", label: "Employees", icon: IdCard, testid: "nav-employees" },
      { to: "/admin/fee-structures", label: "Fee Structure Builder", icon: Wallet, testid: "nav-fee-structures" },
      { to: "/admin/pioneers", label: "Founder Inspiration", icon: Award, testid: "nav-pioneers" },
      { to: "/admin/gallery", label: "Gallery", icon: Images, testid: "nav-gallery" },
      { to: "/admin/hero-slides", label: "Home Hero Slider", icon: Images, testid: "nav-hero-slides" },
      { to: "/admin/job-applications", label: "Job Applications", icon: Briefcase, testid: "nav-job-applications" },
      { to: "/admin/jobs", label: "Jobs (Trust-wide)", icon: Briefcase, testid: "nav-admin-jobs" },
      { to: "/admin/news-events", label: "News and Events", icon: Newspaper, testid: "nav-news-events" },
      { to: "/admin/memories", label: "Share Your Memories", icon: Camera, testid: "nav-memories" },
      { to: "/admin/social-impact", label: "Social Impact", icon: Camera, testid: "nav-social-impact" },
    ],
  },
];

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", module: null },
  { to: "/admin/trust", label: "Trust Details", icon: Building2, testid: "nav-trust", module: "trust" },
  { to: "/admin/executive-body", label: "Executive Body", icon: UserSquare, testid: "nav-executive", module: "executive_body" },
  { to: "/admin/general-body", label: "General Body", icon: Users, testid: "nav-general", module: "general_body" },
  { to: "/admin/meetings", label: "Board Meetings", icon: CalendarRange, testid: "nav-meetings", module: "meetings" },
  { to: "/admin/banks", label: "Bank Accounts", icon: Landmark, testid: "nav-banks", module: "banks" },
  { to: "/admin/banks/institutes", label: "Institute Banks", icon: Landmark, testid: "nav-banks-institutes", module: "banks" },
  { to: "/admin/financials", label: "Financials", icon: BookOpen, testid: "nav-financials", module: "financials" },
  { to: "/admin/documents", label: "Documents", icon: FileText, testid: "nav-documents", module: "documents" },
  { to: "/admin/properties", label: "Properties", icon: MapPin, testid: "nav-properties", module: "immovables" },
  { to: "/admin/gallery", label: "Gallery", icon: Images, testid: "nav-gallery", module: "gallery" },
  { to: "/admin/pioneers", label: "Founder Inspiration", icon: Award, testid: "nav-pioneers", module: null },
  { to: "/admin/news-events", label: "News & Events", icon: Newspaper, testid: "nav-news-events", module: null },
  { to: "/admin/social-impact", label: "Social Impact", icon: Camera, testid: "nav-social-impact", module: null },
  { to: "/admin/memories", label: "Share Your Memories", icon: Camera, testid: "nav-memories", module: null },
  { to: "/admin/vendors", label: "Vendor Empanelment", icon: Building2, testid: "nav-vendors", module: null },
  { to: "/admin/tenders", label: "Tender Management", icon: FileText, testid: "nav-tenders", module: null },
  { to: "/admin/payments", label: "Payments", icon: Wallet, testid: "nav-payments", module: null },
  { to: "/admin/hero-slides", label: "Home Hero Slider", icon: Images, testid: "nav-hero-slides", module: null },
  { to: "/admin/fee-structures", label: "Fee Structure Builder", icon: Wallet, testid: "nav-fee-structures", module: null },
  { to: "/admin/admission-inquiries", label: "Admission Inquiries", icon: Inbox, testid: "nav-admission-inquiries", module: null },
  { to: "/admin/job-applications", label: "Job Applications", icon: Briefcase, testid: "nav-job-applications", module: null },
  { to: "/admin/jobs", label: "Jobs (Trust-wide)", icon: Briefcase, testid: "nav-admin-jobs", module: null },
  { to: "/admin/employees", label: "Employees", icon: IdCard, testid: "nav-employees", module: "employees" },
  { to: "/admin/institutes", label: "Institutes", icon: GraduationCap, testid: "nav-institutes", module: "institutes" },
  { to: "/admin/academic", label: "Academic Reports", icon: BarChart3, testid: "nav-academic", module: "institutes" },
  { to: "/admin/masters", label: "Masters", icon: ListChecks, testid: "nav-masters", module: "masters" },
  { to: "/admin/amenities", label: "Amenities", icon: Sparkles, testid: "nav-amenities", module: "amenities" },
  { to: "/admin/amenity-bookings", label: "Amenity Bookings", icon: Calendar, testid: "nav-amenity-bookings", module: "amenities" },
  { to: "/admin/amenity-vouchers", label: "Waiver Vouchers", icon: Ticket, testid: "nav-amenity-vouchers", module: "amenities" },
  { to: "/admin/vehicles", label: "Vehicle Master", icon: Car, testid: "nav-vehicles", module: "vehicles" },
  { to: "/admin/drivers", label: "Drivers", icon: UserCog, testid: "nav-drivers", module: "vehicles" },
  { to: "/admin/analytics", label: "Site Analytics", icon: BarChart3, testid: "nav-analytics", requireSuperAdmin: true },
  { to: "/admin/users", label: "Users & Roles", icon: ShieldCheck, testid: "nav-users", requireUserMgmt: true },
  { to: "/admin/change-password", label: "Change Password", icon: KeyRound, testid: "nav-change-password", module: null },
];

export default function AdminLayout() {
  const { user, logout, canRead, canManageUsers, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = user?.role === "super_admin";
  const [mobileOpen, setMobileOpen] = useState(false);

  // Groups auto-open when the current URL matches one of their children.
  const initialOpenGroups = useMemo(() => {
    if (!isSuperAdmin) return {};
    const out = {};
    SUPER_ADMIN_NAV.forEach((n) => {
      if (n.kind === "group" && n.children.some((c) => location.pathname.startsWith(c.to))) {
        out[n.label] = true;
      }
    });
    return out;
  }, [isSuperAdmin, location.pathname]);
  const [openGroups, setOpenGroups] = useState(initialOpenGroups);

  useEffect(() => {
    // When the URL changes, make sure the matching group opens (keeps user
    // oriented when navigating via a direct link).
    if (!isSuperAdmin) return;
    setOpenGroups((prev) => {
      const next = { ...prev };
      SUPER_ADMIN_NAV.forEach((n) => {
        if (n.kind === "group" && n.children.some((c) => location.pathname.startsWith(c.to))) {
          next[n.label] = true;
        }
      });
      return next;
    });
  }, [isSuperAdmin, location.pathname]);

  const toggleGroup = (label) => setOpenGroups((g) => ({ ...g, [label]: !g[label] }));

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

// Modules the Infrastructure role is allowed to see in the sidebar. Non-module
// items (module: null) are filtered against this set via `roles` below.
const INFRA_NAV_TESTIDS = new Set(["nav-dashboard", "nav-vendors", "nav-tenders", "nav-payments", "nav-masters", "nav-amenities", "nav-amenity-bookings", "nav-amenity-vouchers"]);
const VEHICLE_ADMIN_NAV_TESTIDS = new Set(["nav-dashboard", "nav-vehicles", "nav-drivers", "nav-change-password"]);

  const visibleNav = navItems.filter((item) => {
    if (item.requireSuperAdmin) return user?.role === "super_admin";
    if (item.requireUserMgmt) return canManageUsers;
    // Infrastructure role only sees tender/vendor/masters.
    if (user?.role === "infrastructure") return INFRA_NAV_TESTIDS.has(item.testid);
    // Vehicle Maintenance Admin sees ONLY dashboard + vehicles + drivers + password.
    if (user?.role === "vehicle_admin") return VEHICLE_ADMIN_NAV_TESTIDS.has(item.testid);
    if (!item.module) return true;
    return canRead(item.module);
  });

  const Sidebar = (
    <aside
      data-testid="admin-sidebar"
      className="w-72 shrink-0 border-r border-[#e5e0d8] bg-[#f9f8f6] flex flex-col"
    >
      <div className="px-6 py-8 border-b border-[#e5e0d8]">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-sm bg-white border border-[#e5e0d8] flex items-center justify-center p-1">
            <img
              src="https://customer-assets.emergentagent.com/job_trust-admin-hub-1/artifacts/i2nc9e38_logo.jpeg"
              alt="GS Mandal crest"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <div className="font-serif text-xl leading-tight text-[#1c1a17]">GS Mandal</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] mt-0.5">
              Gramaudyogik Shikshan Mandal · 1975
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto" data-testid="admin-nav">
        {isSuperAdmin ? (
          SUPER_ADMIN_NAV.map((node) => {
            if (node.kind === "link") {
              const Icon = node.icon;
              return (
                <NavLink
                  key={node.to}
                  to={node.to}
                  end={node.end}
                  data-testid={node.testid}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors ${
                      isActive ? "bg-[#234333] text-white" : "text-[#1c1a17] hover:bg-[#efece6]"
                    }`
                  }
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span>{node.label}</span>
                </NavLink>
              );
            }
            // Group
            const GroupIcon = node.icon;
            const isOpen = !!openGroups[node.label];
            const hasActiveChild = node.children.some((c) => location.pathname.startsWith(c.to));
            return (
              <div key={node.label} data-testid={`${node.testid}-wrap`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(node.label)}
                  data-testid={node.testid}
                  aria-expanded={isOpen}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors ${
                    hasActiveChild && !isOpen
                      ? "bg-[#efece6] text-[#1c1a17] font-semibold"
                      : "text-[#1c1a17] hover:bg-[#efece6]"
                  }`}
                >
                  <GroupIcon size={16} strokeWidth={1.5} />
                  <span className="flex-1 text-left">{node.label}</span>
                  <ChevronRight
                    size={14}
                    className={`transition-transform text-[#8a8478] ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="mt-0.5 mb-1.5 ml-3 pl-3 border-l border-[#e5e0d8] space-y-0.5" data-testid={`${node.testid}-children`}>
                    {node.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          data-testid={child.testid}
                          onClick={() => setMobileOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-colors ${
                              isActive
                                ? "bg-[#234333] text-white"
                                : "text-[#3a362f] hover:bg-[#efece6]"
                            }`
                          }
                        >
                          <ChildIcon size={14} strokeWidth={1.5} />
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors ${
                    isActive ? "bg-[#234333] text-white" : "text-[#1c1a17] hover:bg-[#efece6]"
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.5} />
                <span>{item.label}</span>
              </NavLink>
            );
          })
        )}
      </nav>

      <div className="px-4 py-5 border-t border-[#e5e0d8]">
        <div className="px-2 pb-3">
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b]">Signed in as</div>
          <div
            data-testid="sidebar-username"
            className="font-sans text-sm text-[#1c1a17] mt-0.5"
          >
            {user?.name || user?.username || "—"}
          </div>
          {role ? (
            <div
              data-testid="sidebar-role"
              className="mt-1 inline-block text-[10px] tracking-wide uppercase border border-[#234333]/30 text-[#234333] px-2 py-0.5 rounded-sm bg-white"
            >
              {ROLE_LABEL[role] || role}
            </div>
          ) : null}
        </div>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-2 border border-[#e5e0d8] rounded-sm text-sm text-[#1c1a17] hover:bg-[#efece6] transition-colors"
        >
          <LogOut size={16} strokeWidth={1.5} />
          Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-paper" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">{Sidebar}</div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 bg-[#f9f8f6] shadow-xl">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar mobile */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[#e5e0d8] bg-[#f9f8f6]">
          <div className="font-serif text-lg text-[#1c1a17]">GS Mandal · Admin</div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="p-2 border border-[#e5e0d8] rounded-sm"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
